import * as React from "react";
import { connect } from "react-redux";

import { makeMapStateToQuestionnaireSectionProps, mapDispatchToQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/mappers";
import { getQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";

import { NumberQuestion } from "@teamprevent/common/questionnaires/components/questions/NumberQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { ContextAwareProgressBarQuestionnaireSection } from "@teamprevent/common/questionnaires/components/ContextAwareProgressBarQuestionnaireSection";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";
import { HealthSurveillanceSectionProps } from "@teamprevent/common/healthSurveillance/models";

import * as questionIds from "../consultationQuestions/questionIds";
import { sections } from "../consultation";

const stockholmSensorineuralGradingOptions = [
    { value: "0sn", text: "0sn" },
    { value: "1sn", text: "1sn" },
    { value: "2sn", text: "2sn" },
    { value: "3sn", text: "3sn" }
];

const stockholmVascularGradingOptions = [
    { value: "0sn", text: "0sn" },
    { value: "1v", text: "1v" },
    { value: "2v", text: "2v" },
    { value: "3v", text: "3v" },
    { value: "4v", text: "4v" }
];

const HavsClinicalAssessmentOfHistoryAndExaminationSectionUnconnected: React.FunctionComponent<HealthSurveillanceSectionProps> =
(props: HealthSurveillanceSectionProps) => {
    const enableHealthSurveillanceCrBugs = getFeatureToggles()[FeatureToggleNames.HealthSurveillanceCrBugs];
    return (
        <ContextAwareProgressBarQuestionnaireSection
            title="HAVS Clinical Assessment of History and Examination"
            {...props}
            {...sections.Examination}
        >
            <p><b>If completed by an OHA this is opinion only. An OHP decision will be final.</b></p>
            <MultiButtonQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PrimaryRaynaudsPhenomenonPresent)}
            />
            <MultiButtonQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SecondaryRaynaudsPhenomenonPresent)}
            />
            <MultiButtonQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RaynaudsPhenomenonPresentVibrationInduced)}
            />
            <DropdownQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightStockholmVascularGrading)}
                options={enableHealthSurveillanceCrBugs ? stockholmVascularGradingOptions : stockholmSensorineuralGradingOptions}
            />
            <DropdownQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftStockholmVascularGrading)}
                options={enableHealthSurveillanceCrBugs ? stockholmVascularGradingOptions : stockholmSensorineuralGradingOptions}
            />
            <MultiButtonQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.NeurologicalImpairmentSuggested)}
            />
            <MultiButtonQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.CarpalTunnelSyndromeSuggested)}
            />
            <DropdownQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightStockholmSensorineuralGrading)}
                options={enableHealthSurveillanceCrBugs ? stockholmSensorineuralGradingOptions : stockholmVascularGradingOptions}
            />
            <DropdownQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftStockholmSensorineuralGrading)}
                options={enableHealthSurveillanceCrBugs ? stockholmSensorineuralGradingOptions : stockholmVascularGradingOptions}
            />
            <MultiButtonQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.MuscularSoftTissueDisorderPresent)}
            />
            <MultiButtonQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.EvidenceSkeletalDisorder)}
            />
            <NumberQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VascularLatentPeriods)}
            />
            <NumberQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.NeurologicalLatentPeriods)}
            />
            <NumberQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.MusculoskeletalLatentPeriods)}
            />
            <MultiButtonQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VibrotactileThresholdFurtherInvestigationsRequired)}
            />
            <MultiButtonQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TemperatureThresholdFurtherInvestigationsRequired)}
            />
            <TextQuestion
                readonly={props.readonly}
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AdditionalClinicalNotes)}
                multiline
            />
        </ContextAwareProgressBarQuestionnaireSection>
    );
};

export const HavsClinicalAssessmentOfHistoryAndExaminationSection = connect(
    makeMapStateToQuestionnaireSectionProps(sections.Examination.id),
    mapDispatchToQuestionnaireSectionProps
)(HavsClinicalAssessmentOfHistoryAndExaminationSectionUnconnected);
